<template>
  <div>
    <h5 v-if="showSubtitle" class="text-center info-text mb-5">
      Complete la información del lote, y pulse SIGUIENTE para crearlo
    </h5>

    <div class="row justify-content-center">
      <div class="col-md-3 col-sm-12">
        <div class="form-group">
          <label class="control-label"> Nombre (*) </label>

          <el-input
            v-model="formData.name"
            v-validate="modelValidations.name"
            name="name"
            placeholder="Nombre"
            suffix-icon="fas fa-seedling"
          >
          </el-input>

          <small v-show="name.invalid" class="text-danger">
            {{ getError('name') }}
          </small>
        </div>

        <div v-if="formData.latitude && formData.longitude" class="row">
          <div class="col-sm-12">
            <label class="control-label"> Latitud y Longitud </label>
          </div>
          <div class="col-sm-12">
            <label class="control-label font-weight-bolder"> {{ formData.latitude }}, {{ formData.longitude }} </label>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12 col-sm-6">
            <div class="form-group">
              <label class="control-label"> Hectareas (*) </label>

              <el-input
                v-model="formData.hectares"
                v-validate="modelValidations.hectares"
                name="hectares"
                placeholder="Hectareas"
                suffix-icon="fas fa-solar-panel"
              >
              </el-input>

              <small v-show="hectares.invalid" class="text-danger">
                {{ getError('hectares') }}
              </small>
            </div>
          </div>

          <div class="col-md-12 col-sm-6">
            <div class="form-group">
              <label class="control-label"> Distancia entre surcos (cm.) </label>

              <el-input
                v-model="formData.groovesDistance"
                v-validate="modelValidations.groovesDistance"
                name="groovesDistance"
                placeholder="Distancia entre surcos"
                suffix-icon="fas fa-ruler"
              >
              </el-input>

              <small v-show="groovesDistance.invalid" class="text-danger">
                {{ getError('groovesDistance') }}
              </small>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-sm-12">
            <div class="form-group">
              <label class="control-label"> Tipo de Lote </label>

              <el-select
                v-model="formData.plotType"
                v-validate="modelValidations.plotTypes"
                autocomplete="off"
                filterable
                name="plotType"
                placeholder="Tipo de Lote"
              >
                <el-option
                  v-for="option in plotTypeOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                >
                </el-option>
              </el-select>

              <small v-show="plotType.invalid" class="text-danger">
                {{ getError('plotType') }}
              </small>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label class="control-label"> Cultivo Antecesor (*) </label>

          <el-select
            v-model="formData.predecessor"
            v-validate="modelValidations.predecessor"
            autocomplete="off"
            filterable
            name="predecessor"
            placeholder="Antecesor"
          >
            <el-option
              v-for="option in predecessorsOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            >
            </el-option>
          </el-select>

          <small v-show="predecessor.invalid" class="text-danger">
            {{ getError('predecessor') }}
          </small>
        </div>

        <div class="form-group">
          <label class="control-label"> Campaña (*) </label>

          <el-select
            v-model="formData.campaign"
            v-validate="modelValidations.campaign"
            autocomplete="off"
            filterable
            name="campaign"
            placeholder="Campaña"
          >
            <el-option
              v-for="option in campaignsOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            >
            </el-option>
          </el-select>

          <small v-show="campaign.invalid" class="text-danger">
            {{ getError('campaign') }}
          </small>
        </div>

        <div class="row">
          <div class="col-md-12 col-sm-6">
            <div class="form-group">
              <label class="control-label"> Cultivo (*) </label>

              <el-select
                v-model="formData.crop"
                v-validate="modelValidations.crop"
                autocomplete="off"
                filterable
                name="crop"
                placeholder="Cultivo"
                @change="formData.variety = null"
              >
                <el-option
                  v-for="option in cropsOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                >
                </el-option>
              </el-select>

              <small v-show="crop.invalid" class="text-danger">
                {{ getError('crop') }}
              </small>
            </div>
          </div>

          <div class="col-md-12 col-sm-6">
            <div class="form-group">
              <label class="control-label"> Variedad (*) </label>

              <el-select
                v-model="formData.variety"
                v-validate="modelValidations.variety"
                :disabled="!formData.crop || varietiesOptions.length === 0"
                autocomplete="off"
                filterable
                name="variety"
                placeholder="Variedad"
              >
                <el-option
                  v-for="option in varietiesOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                >
                </el-option>
              </el-select>
              <small v-if="varietiesOptions.length === 0 && formData.crop !== null" class="text-danger">
                No existen variedades para el cultivo seleccionado
              </small>
              <small v-show="variety.invalid" v-else class="text-danger">
                {{ getError('variety') }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-9 col-sm-12">
        <plot-draw-map-limits
          v-show="limitPlot"
          map-id="map-set-limits"
          map-type="satellite"
          entity-type="lote"
          :zoom="12"
          :shape-limit="1"
          :show-palette="false"
          :show-float-buttons="true"
          :default-lat="plot.latitude"
          :default-lng="plot.longitude"
          :default-coordinates="plot.coordinates"
          @shapeAdded="plotSelected"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapFields } from 'vee-validate';
import { mapState } from 'vuex';

import { Select, Option, DatePicker } from 'element-ui';
import PlotDrawMapLimits from '../Map/PlotDrawMapLimits';

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    PlotDrawMapLimits,
  },

  props: {
    showSubtitle: {
      type: Boolean,
      default: true,
    },

    plot: {
      type: Object,
      default: () => {
        return {
          name: '',
          hectares: '',
          latitude: null,
          longitude: null,
          campaign: null,
          predecessor: null,
          crop: null,
          variety: null,
          groovesDistance: '',
          plotType: null,
          coordinates: null,
        };
      },
    },
  },

  data() {
    return {
      limitPlot: true,
      varieties: [],
      plotTypes: [],
      crops: [],
      formData: {
        name: this.plot.name,
        hectares: this.plot.hectares,
        latitude: this.plot.latitude,
        longitude: this.plot.longitude,
        campaign: this.plot.campaign,
        predecessor: this.plot.predecessor,
        crop: this.plot.crop,
        variety: this.plot.variety,
        groovesDistance: this.plot.groovesDistance,
        plotType: this.plot.plotType,
        coordinates: [],
      },

      modelValidations: {
        name: {
          required: true,
          min: 4,
        },

        hectares: {
          required: true,
          min: 0,
          numeric: true,
        },

        predecessor: {
          required: true,
        },

        crop: {
          required: true,
        },

        campaign: {
          required: true,
        },

        variety: {
          required: true,
        },

        latitude: {
          required: false,
          min: -90,
          max: 90,
        },

        longitude: {
          required: false,
          min: -180,
          max: 180,
        },

        groovesDistance: {
          regex: /[+]?([0-9]*[.])?[0-9]+/,
        },

        plotType: {
          required: false,
        },
      },
    };
  },

  computed: {
    ...mapState('models/campaign', ['campaigns']),

    ...mapFields([
      'name',
      'hectares',
      'predecessor',
      'crop',
      'variety',
      'campaign',
      'groovesDistance',
      'latitude',
      'longitude',
      'plotType',
    ]),

    campaignsOptions() {
      return this.$selectOptions(this.campaigns, { sort: true });
    },

    cropsOptions() {
      return this.$selectOptions(this.crops, { sort: true });
    },

    plotTypeOptions() {
      return this.$selectOptions(this.plotTypes, { label: 'label', value: 'id', sort: true });
    },

    predecessorsOptions() {
      return this.$selectOptions(this.crops, { sort: true });
    },

    varietiesOptions() {
      let varieties = this.varieties;
      if (this.formData.crop) {
        varieties = varieties.filter((variety) => variety.cropId === this.formData.crop);
      }
      return this.$selectOptions(varieties, { sort: true });
    },
  },

  async created() {
    const crops = this.fetchCrops();
    const varieties = this.fetchVarieties();
    const plotTypes = this.fetchPlotTypes();

    await Promise.all([crops, plotTypes, varieties]);
  },

  methods: {
    fetchCrops() {
      return this.$services
        .resource('crops')
        .get()
        .then((res) => res.data.data)
        .then((data) => (this.crops = data.crops));
    },

    fetchVarieties() {
      return this.$services
        .resource('varieties')
        .get({ include: 'crop' })
        .then((res) => res.data.data)
        .then((data) => (this.varieties = data.varieties));
    },

    fetchPlotTypes() {
      return this.$services
        .resource('plot_types')
        .get()
        .then((res) => res.data.data)
        .then((data) => (this.plotTypes = data.types));
    },

    getError(fieldName) {
      return this.errors.first(fieldName);
    },

    validate() {
      return this.$validator.validateAll();
    },

    create(field) {
      this.formData.field = field;
      return this.$services.resource('plots').create(this.formData);
    },

    get() {
      return this.formData;
    },

    plotSelected(coordinates, center) {
      this.formData.coordinates = coordinates;
      this.formData.latitude = center.lat;
      this.formData.longitude = center.lng;
    },

    infoWindow(plot, marker) {
      let template = '';
      marker.title = plot.name || 'Nuevo lote';

      if (plot && plot.campaign) {
        let crop = '';
        const campaign = plot.campaign.name || this.campaigns.find((c) => c.id === plot.campaign);

        if (plot.crop) {
          crop = campaign.crops.find((c) => c.id === plot.crop).name;
        }

        template += `<p class="mt-2 border-top border-dark">Campaña: ${campaign.name} ${crop}</p>`;
      }

      template += `<p class="mb-0 small">${marker.info.formatted_address}</p>`;

      if (plot && plot.latitude && plot.longitude) {
        template += `<hr class="m-1"><p class="mb-0 small">${plot.latitude},${plot.longitude}</p>`;
      }

      return template;
    },
  },
};
</script>

<style lang="scss" scoped>
.form-group .el-date-editor--date {
  width: 100%;
}

::v-deep .floating-panel {
  top: 1%;
  right: 20%;
}
</style>
